import { RISK_LOWER_THRESHOLD, RISK_UPPER_THRESHOLD } from '@/constants/risks';
import { ROUTE_ACTION_PLAN } from '@/constants/routes';
import { formatRiskRound, getRoute } from '@/utils';
import { Badge, Box, Flex, Link, Text } from '@cmpsr/components';
import { Link as ReactRouterLink } from 'react-router-dom';
import DashboardHeaderChart, { ChartSeriesData } from './components/dashboard-header-chart/dashboard-header-chart';

type DashboardHeaderSummaryCardProps = {
  currentRisk: number;
  averageRisk: number;
  potentialRisk: number;
  averageRiskPercentage: number;
  series: ChartSeriesData[];
};

export default function DashboardHeaderSummaryCard(props: DashboardHeaderSummaryCardProps) {
  const riskReductionPotential = (Math.abs(props.currentRisk - props.potentialRisk) / props.potentialRisk) * 100;

  const riskBadgeStyle = _getRiskBadgeStyle({
    currentRisk: props.currentRisk,
    averageRisk: props.averageRisk,
  });

  return (
    <Box
      py={{ base: 4, md: 6 }}
      px={{ base: 5, md: 6 }}
      border="1px solid"
      borderColor="medBlue"
      borderRadius="radii-3"
      bg="gradient.linear"
    >
      <Flex
        flexDirection={{ base: 'column', md: 'row' }}
        alignItems={{ base: 'flex-start', md: 'center' }}
        gap={{ base: 3, md: 5 }}
        mb={6}
      >
        <Text variant="text-header-4XL" color="tan" fontSize={{ base: '3.125rem', lg: '4.6875rem' }}>
          {formatRiskRound(props.currentRisk, 'overall')}
        </Text>
        <Box>
          <Text variant="text-body-regular" color="tan" textTransform="uppercase" mb={4}>
            Your lifetime cancer risk
          </Text>
          <Badge
            variant="subtle"
            textTransform="none"
            py={1}
            px={2}
            mb={4}
            bgColor={riskBadgeStyle.bgColor}
            color={riskBadgeStyle.color}
          >
            {riskBadgeStyle.text}
          </Badge>
          <Text variant="text-body-meta-regular" color="tan">
            You can{' '}
            <Link as={ReactRouterLink} to={getRoute(ROUTE_ACTION_PLAN)} variant="link-primary">
              <Text as="span" variant="text-body-meta-regular" color="alert-success-default">
                reduce your risk by {formatRiskRound(riskReductionPotential, 'overall')}
              </Text>
            </Link>{' '}
            through lifestyle factors.
          </Text>
        </Box>
      </Flex>
      <DashboardHeaderChart series={props.series} />
    </Box>
  );
}

function _getRiskBadgeStyle(args: { currentRisk: number; averageRisk: number }) {
  const { currentRisk, averageRisk } = args;

  if (currentRisk > averageRisk * RISK_UPPER_THRESHOLD) {
    return {
      bgColor: 'var(--chakra-colors-alert-error-default)',
      color: 'var(--chakra-colors-text-error)',
      text: `Above Average Risk`,
    };
  } else if (currentRisk < averageRisk * RISK_LOWER_THRESHOLD) {
    return {
      bgColor: 'var(--chakra-colors-alert-success-default)',
      color: 'var(--chakra-colors-text-success)',
      text: `Below Average Risk`,
    };
  } else {
    return {
      bgColor: 'var(--chakra-colors-alert-warning-default)',
      color: 'var(--chakra-colors-text-warning)',
      text: `Average Risk`,
    };
  }
}
